import { FC, ReactElement, useMemo } from 'react'

import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'

import { MAX_TABLET_WIDTH } from 'src/Styled/variables'
import { CampaignPaymentState } from 'src/Redux/artistOrderApi/campaignPayment'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
} from 'src/Types/campaigns'

import { CTAButton } from '../Buttons/CTAButton'
import { DetailsButton } from '../Buttons/DetailsButton'
import { GreyTextButton } from '../Buttons/GreyTextButton'

import { CampaignListItemProps } from '.'

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    width: 200px;
    justify-content: flex-end;
    flex-shrink: 0;
  }
`

const StyledGreyTextButton = styled(GreyTextButton)`
  max-width: 160px;
  padding: 8px 16px;
  width: 100%;

  svg {
    path {
      fill: ${({ theme }) => theme.color.brandGreen};
    }
  }
`

const StyledDetailsButton = styled(DetailsButton)`
  max-width: 160px;
  padding: 8px 16px;
  width: 100%;
`

const StyledCTAButton = styled(CTAButton)`
  max-width: 160px;
  padding: 8px 16px;
  width: 100%;
`

const EmptyButton = styled.div`
  min-width: 48px;
  width: 48px;
  height: 48px;

  @media screen and (max-width: ${MAX_TABLET_WIDTH}) {
    display: none;
  }
`

type CampaignListItemButtonsProps = Pick<
  CampaignListItemProps,
  | 'status'
  | 'platform'
  | 'onRelaunchCampaignClick'
  | 'onStartCampaignClick'
  | 'onDetailsClick'
  | 'onAddTrackLinkClick'
  | 'onSmsNotifyClick'
  | 'newRelease'
  | 'startLoading'
  | 'relaunchLoading'
  | 'relaunched'
  | 'hasNewRelease'
  | 'matchingType'
  | 'paymentState'
  | 'retryPaymentLoading'
  | 'onRetryPaymentClick'
  | 'isPreRelease'
  | 'releaseDatePassed'
  | 'hasTrackLinkSubmitted'
  | 'hasReceivedPlacements'
>

const finishedStatuses = [CampaignStatusEnum.ENDED, CampaignStatusEnum.STOPPED]

const startCampaignStatuses = [
  CampaignStatusEnum.APPROVED,
  CampaignStatusEnum.PENDING,
]

const isRelaunchEligible = (
  platform: CampaignPlatformTypeEnum,
  relaunched: boolean | undefined,
  status: CampaignStatusEnum,
  matchingType: string | null | undefined,
  isPreRelease: boolean | undefined,
  hasTrackLinkSubmitted?: boolean,
  hasReceivedPlacements?: boolean,
): boolean => {
  const isSpotify = platform === CampaignPlatformTypeEnum.SPOTIFY && !relaunched
  if (
    !isSpotify ||
    !finishedStatuses.includes(status) ||
    matchingType === 'manual'
  ) {
    return false
  }
  if (!isPreRelease) {
    return true
  }
  return (
    status === CampaignStatusEnum.ENDED &&
    Boolean(hasTrackLinkSubmitted) &&
    Boolean(hasReceivedPlacements)
  )
}

const smsNotifyStatuses = [
  CampaignStatusEnum.PENDING_APPROVAL,
  CampaignStatusEnum.AWAITING_REVIEW,
]

const isSmsNotifyEligible = (
  status: CampaignStatusEnum,
  onSmsNotifyClick?: () => void,
): boolean => Boolean(onSmsNotifyClick) && smsNotifyStatuses.includes(status)

const renderDetailsButton = (
  isDesktop: boolean,
  newRelease?: boolean,
  onDetailsClick?: () => void,
): ReactElement => {
  if (newRelease) {
    return <EmptyButton />
  }

  if (isDesktop) {
    return (
      <DetailsButton
        variant='icon'
        data-testid='details-button'
        onClick={onDetailsClick}
      />
    )
  }

  return <StyledDetailsButton onClick={onDetailsClick} />
}

const isAddTrackLinkEligible = (
  status: CampaignStatusEnum,
  isPreRelease?: boolean,
  releaseDatePassed?: boolean,
  hasTrackLinkSubmitted?: boolean,
  hasReceivedPlacements?: boolean,
): boolean => {
  if (hasTrackLinkSubmitted) {
    return false
  }
  if (status === CampaignStatusEnum.RUNNING) {
    return Boolean(isPreRelease) && Boolean(releaseDatePassed)
  }
  return (
    status === CampaignStatusEnum.ENDED &&
    Boolean(releaseDatePassed) &&
    Boolean(hasReceivedPlacements)
  )
}

export const CampaignListItemButtons: FC<CampaignListItemButtonsProps> = ({
  status,
  platform,
  newRelease,
  relaunchLoading,
  relaunched,
  startLoading,
  hasNewRelease,
  onRelaunchCampaignClick,
  onStartCampaignClick,
  onDetailsClick,
  onAddTrackLinkClick,
  onSmsNotifyClick,
  matchingType,
  paymentState,
  retryPaymentLoading,
  onRetryPaymentClick,
  isPreRelease,
  releaseDatePassed,
  hasTrackLinkSubmitted,
  hasReceivedPlacements,
}) => {
  const isDesktop = useMediaQuery({ minWidth: MAX_TABLET_WIDTH })

  const ButtonVariant = useMemo(() => {
    if (hasNewRelease) {
      return newRelease ? StyledCTAButton : StyledGreyTextButton
    }

    return StyledCTAButton
  }, [hasNewRelease, newRelease])

  const detailsButton = useMemo(
    () => renderDetailsButton(isDesktop, newRelease, onDetailsClick),
    [isDesktop, newRelease, onDetailsClick],
  )

  if (paymentState === CampaignPaymentState.Failed) {
    return (
      <Container>
        <StyledGreyTextButton
          loading={retryPaymentLoading}
          disabled={retryPaymentLoading}
          onClick={onRetryPaymentClick}
        >
          Retry payment
        </StyledGreyTextButton>

        {detailsButton}
      </Container>
    )
  }

  if (paymentState === CampaignPaymentState.InProgress) {
    return <Container>{detailsButton}</Container>
  }

  if (startCampaignStatuses.includes(status)) {
    return (
      <Container>
        <ButtonVariant loading={startLoading} onClick={onStartCampaignClick}>
          Start campaign
        </ButtonVariant>

        {detailsButton}
      </Container>
    )
  }

  const showAddTrackLink = isAddTrackLinkEligible(
    status,
    isPreRelease,
    releaseDatePassed,
    hasTrackLinkSubmitted,
    hasReceivedPlacements,
  )

  if (showAddTrackLink) {
    return (
      <Container>
        <StyledCTAButton onClick={onAddTrackLinkClick}>
          Add track link
        </StyledCTAButton>

        {detailsButton}
      </Container>
    )
  }

  const isStoppedPreRelease =
    isPreRelease && status === CampaignStatusEnum.STOPPED

  if (isStoppedPreRelease) {
    return <Container>{detailsButton}</Container>
  }

  if (
    isRelaunchEligible(
      platform,
      relaunched,
      status,
      matchingType,
      isPreRelease,
      hasTrackLinkSubmitted,
      hasReceivedPlacements,
    )
  ) {
    return (
      <Container>
        <StyledGreyTextButton
          loading={relaunchLoading}
          onClick={onRelaunchCampaignClick}
        >
          Relaunch
        </StyledGreyTextButton>

        {detailsButton}
      </Container>
    )
  }

  if (isSmsNotifyEligible(status, onSmsNotifyClick)) {
    return (
      <Container>
        <StyledCTAButton onClick={onSmsNotifyClick}>
          SMS on Approval
        </StyledCTAButton>
        {detailsButton}
      </Container>
    )
  }

  return <Container>{detailsButton}</Container>
}
